/**
 * The close card — the last action of a release, and the only irreversible one on this
 * page: it force-moves `deployed/server` onto the release, which is what makes the
 * next release's diff start here.
 *
 * The preconditions are listed live (DESIGN §14) from the same `closeBlockers()` the
 * host re-checks, so the card cannot offer a close the host would refuse, or refuse one
 * it would allow. Two of them are things an operator may legitimately close through —
 * an unannounced release, a merge-back that has not landed — and each is its own
 * checkbox rather than a general override, because they are different decisions:
 * skipping the announcement needs a reason on the record, while the merge-back is a job
 * the prepare host will finish on its own.
 *
 * The other two have no checkbox on purpose, and for the same reason: the way past each
 * is a record of what actually happened, made where the thing itself is. An outstanding
 * component is deployed or its skip acknowledged in the table above; a changed Pulumi
 * program is accounted for on the Infrastructure card. A checkbox here would let a
 * release close with changed code silently missing from production, or with the
 * infrastructure it assumes never created.
 */
import { useState } from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    CLOSE_BLOCKER,
    INSTRUCTION_KIND,
    closeBlockers,
    closeHints,
    legalInstructions,
    narrationFor,
    type Phase,
    type ReleaseDoc,
} from '@finvari/deployer';
import { deployProgress, type ComponentRow } from '../lib/componentRows';
import { useInstructionAction } from '../hooks';
import { ActivityAlert } from './ActivityAlert';
import { FactList } from './FactList';

export function CloseReleaseCard({
    release,
    rows,
    phase,
}: {
    release: ReleaseDoc;
    rows: ComponentRow[];
    phase: Phase;
}) {
    const [allowUnannounced, setAllowUnannounced] = useState(false);
    const [acknowledgeMergeBack, setAcknowledgeMergeBack] = useState(false);
    const [reason, setReason] = useState('');

    // The browser's half of the shared check: the host reads `deploy.log` in the
    // checkout, this reads the markers the host projected from it.
    const deployed = new Set(rows.filter((r) => r.state === 'deployed').map((r) => r.name));
    const params = { allowUnannounced, acknowledgeMergeBack, reason: reason.trim() || undefined };
    const blockers = closeBlockers(release, deployed, params);
    // Advice, not a condition: a compile-only component that was never built is the
    // one thing this release can leave undone that nothing downstream would catch.
    const hints = closeHints(release, new Set(rows.filter((r) => r.compiled).map((r) => r.name)));
    const narration = narrationFor(INSTRUCTION_KIND.releaseClose);

    const phaseReason = legalInstructions(phase, 'release').find((l) => l.kind === INSTRUCTION_KIND.releaseClose)
        ?.reasonIfIllegal;
    // A reason is required by the schema when closing unannounced, and the host would
    // reject the instruction — so the button says so rather than letting it round-trip.
    const reasonMissing = allowUnannounced && reason.trim().length === 0;
    const disabledReason =
        phaseReason ??
        (blockers.length > 0 ? 'Clear the conditions below first.' : undefined) ??
        (reasonMissing ? 'Give a reason for closing without an announcement.' : undefined);
    const action = useInstructionAction('release', INSTRUCTION_KIND.releaseClose, {
        params,
        release: release.version,
        disabledReason,
    });

    const progress = deployProgress(rows);
    // Mentioned only when there is one, so the usual release's fact list stays short.
    const infraChanges = release.infraChanges ?? [];
    const infraAccountedFor = infraChanges.filter((c) => release.infraAcknowledgements?.[c.program]).length;

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={2}>
                    <Stack spacing={0.5}>
                        <Typography variant="h6">Close {release.version}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {narration.what}
                        </Typography>
                    </Stack>

                    <FactList
                        facts={[
                            {
                                label: 'Components',
                                value:
                                    progress.changed === 0
                                        ? 'This release changes no deployable component.'
                                        : `${progress.deployed} deployed, ${progress.acknowledged} skipped, ${progress.outstanding} outstanding of ${progress.changed} changed.`,
                            },
                            ...(infraChanges.length > 0
                                ? [
                                      {
                                          label: 'Infrastructure',
                                          value: `${infraAccountedFor} of ${infraChanges.length} changed Pulumi program${infraChanges.length === 1 ? '' : 's'} accounted for — nothing in this release applies them.`,
                                      },
                                  ]
                                : []),
                            {
                                label: 'Announcement',
                                value: release.announcement?.postedAt ? 'Posted to Slack.' : 'Not posted.',
                            },
                            {
                                label: 'Merge-back',
                                value: release.mergeBackObserved
                                    ? 'origin/main contains this release.'
                                    : 'Not observed on origin/main yet.',
                            },
                            ...(progress.compileOnly > 0
                                ? [
                                      {
                                          label: 'Compile only',
                                          value: `${progress.compileOnly} rebuilt with no runtime path to a changed file — no deploy needed.`,
                                      },
                                  ]
                                : []),
                            { label: 'Reversible', value: narration.reversible },
                        ]}
                    />

                    {blockers.length > 0 && (
                        <Alert severity="warning">
                            <AlertTitle>Not ready to close</AlertTitle>
                            <Stack spacing={0.5}>
                                {blockers.map((b) => (
                                    <Typography key={b.code} variant="body2">
                                        {b.message}
                                    </Typography>
                                ))}
                            </Stack>
                        </Alert>
                    )}

                    {hints.length > 0 && (
                        <Alert severity="info">
                            <AlertTitle>Worth doing before you close</AlertTitle>
                            <Stack spacing={0.5}>
                                {hints.map((h) => (
                                    <Typography key={h.code} variant="body2">
                                        {h.message}
                                    </Typography>
                                ))}
                            </Stack>
                        </Alert>
                    )}

                    {/*
                      * Offered whether or not the blocker is currently listed, so ticking
                      * one does not make the box it was ticked in disappear.
                      */}
                    {!release.announcement?.postedAt && (
                        <Stack>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allowUnannounced}
                                        onChange={(e) => setAllowUnannounced(e.target.checked)}
                                    />
                                }
                                label="Close without announcing it"
                            />
                            {allowUnannounced && (
                                <TextField
                                    label="Why this release is closing unannounced"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    helperText="Kept on the release record — this is the answer to a question someone asks months later."
                                />
                            )}
                        </Stack>
                    )}

                    {!release.mergeBackObserved && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={acknowledgeMergeBack}
                                    onChange={(e) => setAcknowledgeMergeBack(e.target.checked)}
                                />
                            }
                            label="Close although origin/main does not have the release yet"
                        />
                    )}

                    {action.error && <Alert severity="error">{action.error}</Alert>}
                    {action.latest && action.activity && (
                        <ActivityAlert
                            host="release"
                            row={action.latest}
                            line={action.activity}
                            label={narration.title}
                        />
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="contained"
                            color="warning"
                            disabled={!!action.disabledReason || action.sending}
                            onClick={action.run}
                        >
                            Close {release.version}
                        </Button>
                        {action.disabledReason && (
                            <Typography variant="caption" color="text.secondary">
                                {action.disabledReason}
                            </Typography>
                        )}
                    </Stack>

                    {blockers.some((b) => b.code === CLOSE_BLOCKER.componentsOutstanding) && (
                        <Typography variant="caption" color="text.secondary">
                            Deploy the outstanding components above, or acknowledge each skip with a reason. There is no
                            override for this one: a release that closes with changed code left out has to say which
                            code and why.
                        </Typography>
                    )}

                    {blockers.some((b) => b.code === CLOSE_BLOCKER.infraOutstanding) && (
                        <Typography variant="caption" color="text.secondary">
                            Acknowledge each program on the Infrastructure card above, saying what was done — the{' '}
                            <code>pulumi up</code> an owner ran, or that the change needed none. No override, and not
                            covered by closing unannounced: that is about telling people, and this is about whether the
                            infrastructure the release assumes exists at all.
                        </Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
